import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from './firebase';

/**
 * Gera um nome único para o arquivo
 * @param {string} originalName - Nome original do arquivo
 * @returns {string} Nome único para o arquivo
 */
const generateUniqueFileName = (originalName) => {
  const timestamp = Date.now();
  const extension = originalName.split('.').pop();
  return `${timestamp}.${extension}`;
};

/**
 * Extrai o caminho do arquivo a partir da URL do Firebase Storage
 * @param {string} url - URL completa do arquivo
 * @returns {string|null} Caminho do arquivo ou null se a URL não for válida
 */
const getPathFromUrl = (url) => {
  try {
    if (!url) return null;
    console.log('Tentando extrair caminho da URL:', url);
    
    // Tentar diferentes padrões de URL do Firebase Storage
    
    // Padrão 1: URL completa com parâmetros
    // https://firebasestorage.googleapis.com/v0/b/[bucket]/o/[path]?[params]
    let matches = url.match(/\/o\/(.+?)\?/);
    if (matches && matches[1]) {
      const path = decodeURIComponent(matches[1]);
      console.log('Caminho extraído (padrão 1):', path);
      return path;
    }

    // Padrão 2: URL sem parâmetros
    // https://firebasestorage.googleapis.com/v0/b/[bucket]/o/[path]
    matches = url.match(/\/o\/(.+)$/);
    if (matches && matches[1]) {
      const path = decodeURIComponent(matches[1]);
      console.log('Caminho extraído (padrão 2):', path);
      return path;
    }

    // Padrão 3: Caminho direto do storage
    // salons/[salonId]/[path]/[filename]
    if (url.startsWith('salons/')) {
      console.log('Caminho extraído (padrão 3):', url);
      return url;
    }

    console.error('Nenhum padrão de URL reconhecido');
    return null;
  } catch (error) {
    console.error('Erro ao extrair caminho da URL:', error);
    return null;
  }
};

/**
 * Faz upload de um arquivo para o Firebase Storage
 * @param {string} salonId - ID do salão
 * @param {File} file - Arquivo a ser enviado
 * @param {string} path - Caminho dentro da pasta do salão (ex: 'logo', 'images', etc)
 * @returns {Promise<{url: string, path: string}>} URL e caminho do arquivo enviado
 */
export const uploadSalonFile = async (salonId, file, path) => {
  try {
    if (!salonId || !file) {
      throw new Error('salonId e file são obrigatórios');
    }

    // Gerar um nome único para o arquivo
    const uniqueFileName = generateUniqueFileName(file.name);
    
    // Criar o caminho completo do arquivo
    const filePath = `salons/${salonId}/${path}/${uniqueFileName}`;
    console.log('Criando arquivo em:', filePath);
    
    // Criar referência para o arquivo
    const fileRef = ref(storage, filePath);

    // Fazer upload do arquivo
    await uploadBytes(fileRef, file);

    // Obter a URL do arquivo
    const downloadURL = await getDownloadURL(fileRef);

    return {
      url: downloadURL,
      path: filePath
    };
  } catch (error) {
    console.error('Erro ao fazer upload do arquivo:', error);
    throw error;
  }
};

/**
 * Remove um arquivo do Firebase Storage
 * @param {string} fileUrlOrPath - URL ou caminho do arquivo a ser removido
 * @returns {Promise<boolean>} true se removido com sucesso
 */
export const deleteSalonFile = async (fileUrlOrPath) => {
  try {
    if (!fileUrlOrPath) return false;
    console.log('Tentando remover arquivo:', fileUrlOrPath);

    let filePath;
    
    // Verificar se é um caminho direto do storage
    if (fileUrlOrPath.startsWith('salons/')) {
      console.log('Usando caminho direto do storage');
      filePath = fileUrlOrPath;
    } else {
      // Se não for um caminho direto, tentar extrair da URL
      filePath = getPathFromUrl(fileUrlOrPath);
    }

    if (!filePath) {
      console.error('Caminho do arquivo inválido:', fileUrlOrPath);
      return false;
    }

    console.log('Removendo arquivo do caminho:', filePath);
    
    // Criar referência usando o caminho extraído
    const fileRef = ref(storage, filePath);

    // Remover o arquivo
    await deleteObject(fileRef);
    console.log('Arquivo removido com sucesso');

    return true;
  } catch (error) {
    console.error('Erro ao remover arquivo:', error);
    return false;
  }
}; 